import { useAtom } from "jotai";
import React, { useEffect, useState } from "react";

import Drawer from "@/components/shared/drawer";
import ProblemsCard from "@/components/shared/narrowTaskCard/problemsCard";
import {
  memoDrawerOpenAtom,
  memoTargetAtom
} from "@/states/owner/task/problems/memoAtom";
import ProblemViewDataProc from "@/services/owner/task/problemViewDataProc";
import { useProblemMemo } from "@/hooks/owner/task/useProblemMemo";
import ProblemMemoInput from "@/components/pages/owner/task/components/problemMemo/input";
import ProblemMemoSkeleton from "@/components/pages/owner/task/components/problemMemo/skeleton";

import type {
  TaskRptFinal,
  Rptproblems,
  V2taskData
} from "@/types/owner/task/v2task";

type Props = {
  taskData: V2taskData[];
  isDetailPage?: boolean;
};

/* メモ機能ドロワー */
const ProblemMemoDrawer: React.FC<Props> = ({ taskData, isDetailPage }) => {
  const [showMemoDrawer, setShowMemoDrawer] = useAtom(memoDrawerOpenAtom);
  const [memoTarget, setMemoTarget] = useAtom(memoTargetAtom);
  const [showSavedText, setShowSavedText] = useState(false);
  const {
    rptName,
    taskrpts,
    taskRptFinals,
    memoData,
    isLoading,
    isSaveLoading,
    setTaskData
  } = useProblemMemo();

  // 異常チェックリストのカテゴリーごとに通番を振る
  const checkProblemChecklistIndex = (
    taskrptFinal: TaskRptFinal,
    rptproblem: Rptproblems,
    rptproblemIndex: number
  ) => {
    const proc = new ProblemViewDataProc(taskrptFinal, rptproblem);
    return proc.checkProblemChecklistIndex(rptproblemIndex);
  };

  //タスクデータをセットしてカスタムフックを発火
  useEffect(() => {
    if (taskData) setTaskData(taskData);
  }, [taskData]);

  //メモドロワーが閉じた際にメモターゲットをリセット
  useEffect(() => {
    if (!showMemoDrawer) setMemoTarget(null);
  }, [showMemoDrawer]);

  //保存中のステータスが変わった際に保存完了テキストを表示
  useEffect(() => {
    if (isSaveLoading) {
      setShowSavedText(true);
      setTimeout(() => {
        setShowSavedText(false);
      }, 2000);
    }
  }, [isSaveLoading]);

  return (
    <Drawer
      title={rptName}
      isDrawerOpen={showMemoDrawer}
      setIsDrawerOpen={setShowMemoDrawer}
      rightHeaderLabel={
        <p
          className={`text-cdisabled ${
            showSavedText
              ? "animate__animated animate__fadeIn"
              : "animate__animated animate__fadeOut"
          }`}
        >
          保存しました
        </p>
      }
    >
      <div className="m-16 mb-[7rem] h-full space-y-16">
        {isLoading ? (
          <>
            <ProblemMemoSkeleton />
            <ProblemMemoSkeleton />
          </>
        ) : (
          <>
            {/* 通常箇所 - 報告確認画面から開いた場合は選択した箇所のみ表示 - */}
            {taskrpts?.map((taskrpt) => (
              <React.Fragment key={`problem-${taskrpt.id}`}>
                {taskrpt.tasksubrpts?.length > 0 &&
                  (isDetailPage
                    ? taskrpt.tasksubrpts.filter(
                        (subrpt) => subrpt.post_type === "comments"
                      )[0].comments
                    : taskrpt.tasksubrpts[0].comments
                  ).map((tasksubrptComments, tasksubrptCommentsIndex) => (
                    <React.Fragment
                      key={`problemDetail-${taskrpt.id}-${tasksubrptComments.id}`}
                    >
                      {(memoTarget?.commentId === tasksubrptComments.id ||
                        !memoTarget?.commentId) && (
                        <>
                          <div className="space-y-12">
                            <ProblemsCard
                              taskrpt={taskrpt}
                              tasksubrptComments={tasksubrptComments}
                              rptproblemIndex={tasksubrptCommentsIndex}
                              isMemoInputMode
                            />
                            <ProblemMemoInput
                              memoData={memoData}
                              taskrpt={taskrpt}
                              tasksubrptCommentsId={tasksubrptComments.id}
                            />
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
            {/* その他異常箇所 - 報告確認画面から開いた場合は選択した箇所のみ表示 - */}
            {taskRptFinals?.map((taskrptFinal) => (
              <React.Fragment key={`problemFinal-${taskrptFinal.id}`}>
                {taskrptFinal.rptproblems?.map(
                  (rptproblem, rptproblemIndex) => (
                    <React.Fragment
                      key={`problemFinalDetail-${taskrptFinal.id}-${rptproblem.id}`}
                    >
                      {(rptproblem.content.content ||
                        rptproblem.content.name) &&
                        (memoTarget?.commentId ===
                          rptproblem.problem_comment?.id ||
                          !memoTarget?.commentId) && (
                          <div className="space-y-12">
                            <ProblemsCard
                              rptproblem={rptproblem}
                              rptproblemtypes={taskrptFinal.rptproblemtypes}
                              taskId={taskrptFinal.task_id}
                              rptproblemIndex={checkProblemChecklistIndex(
                                taskrptFinal,
                                rptproblem,
                                rptproblemIndex
                              )}
                              isMemoInputMode
                            />
                            <ProblemMemoInput
                              memoData={memoData}
                              rptproblem={rptproblem}
                              taskrptFinal={taskrptFinal}
                            />
                          </div>
                        )}
                    </React.Fragment>
                  )
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ProblemMemoDrawer;
